/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { documentTypesApiService } from '../../../services/api/api.documenttypes'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

interface FilterFormValues {
  types: any[]
}

const persistenceKey = 'settingsDocumentTypesFilter'

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    types: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(getDefaultFilter())

  useEffect(() => {
    // Load persisted filter on mount
    const persistedFilter = context.service.persist.getGlobal<FilterFormValues>(persistenceKey)
    if (persistedFilter && persistedFilter.types && persistedFilter.types.length > 0) {
      setFormValues(persistedFilter)
      tableData.applyFilter(persistedFilter)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setGlobal(persistenceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const isFilterDefault = (filter: FilterFormValues = formValues) => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(filter) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-sm-6 col-lg-4">
              <div className="form-group" id="9766d96c-ef43-41b4-821e-f932f02b54bb">
                <label className="control-label">{translate('TYPES_TITLE')}</label>
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_DOC_TYPES_PLACEHOLDER...')}
                  name="types"
                  value={formValues.types}
                  asyncLoadFn={async (search: string, page: number) => {
                    try {
                      return await documentTypesApiService.getOptionsForAsyncPicker(search, page)
                    } catch (error) {
                      console.error('Error loading document types for filter:', error)
                      return {
                        options: [],
                        hasMore: false
                      }
                    }
                  }}
                  onChange={(option: any) => {
                    updateField('types', option ? option : [])
                  }}
                  multi={true}
                  searchable
                  id="c980b30c-582d-42f3-afe8-753305ef7ca9"
                />
              </div>
            </div>
          </div>
          <div>
            <button
              data-action="filter"
              className="btn btn-primary"
              onClick={handleApplyFilter}
              id="f749e8f5-2cb6-409a-9fc2-4aadd9a70504"
            >
              {translate('BTN_FILTER')}
            </button>
            <button
              data-action="reset"
              disabled={isFilterDefault()}
              className="btn btn-chita btn-link"
              onClick={handleClearFilter}
              id="68ce2f41-34cc-4f2f-8f70-0241adeb8844"
            >
              {translate('BTN_CLEAR_FILTER')}
            </button>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
